import { forwardRef } from 'react'

import styles from './Button.module.css'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button visual variant */
  variant?: 'primary' | 'secondary' | 'ghost'
  /** Button size */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Show loading spinner */
  isLoading?: boolean
  /** Stretch to full container width */
  isFullWidth?: boolean
  /** Fixed width in pixels (for centered buttons) */
  fixedWidth?: number
  /** Icon on the left */
  leftIcon?: React.ReactNode
  /** Icon on the right */
  rightIcon?: React.ReactNode
  /** Button content */
  children?: React.ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      isFullWidth = false,
      fixedWidth,
      leftIcon,
      rightIcon,
      children,
      className,
      disabled,
      style,
      ...props
    },
    ref
  ) => {
    const classNames = [
      styles.button,
      styles[variant],
      styles[size],
      isFullWidth && styles.fullWidth,
      isLoading && styles.loading,
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const buttonStyle = fixedWidth ? { ...style, width: `${fixedWidth}px` } : style

    return (
      <button
        ref={ref}
        className={classNames}
        disabled={disabled || isLoading}
        style={buttonStyle}
        {...props}
      >
        {isLoading && <span className={styles.spinner} />}
        {!isLoading && leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}
        <span className={styles.content}>{children}</span>
        {!isLoading && rightIcon && <span className={styles.rightIcon}>{rightIcon}</span>}
      </button>
    )
  }
)

Button.displayName = 'Button'
